import React, { useContext } from 'react';
import { RendererContext } from '../../context';
import { functionWithFG } from '@atlaskit/platform-feature-flags-react';
import { Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';
import Link from '@atlaskit/link';
import { defineMessages, useIntl } from 'react-intl';

type EgressErrorMessageProps = {
	url: string;
};

const messages = defineMessages({
	pleaseUseThe: {
		id: 'forge-ui.egressErrorMessage.please-use-the',
		defaultMessage: 'Please use the',
		description: 'Use data image scheme for an image source message part 1',
	},
	dataImage: {
		id: 'forge-ui.egressErrorMessage.data-image',
		defaultMessage: 'data:image',
		description: 'Use data image scheme for an image source part 2',
	},
	schemeForAnImageSource: {
		id: 'forge-ui.egressErrorMessage.scheme-for-an-image-source',
		defaultMessage: 'scheme for an image source',
		description: 'Use data image scheme for an image source part 3',
	},
	egressPermissionsFor: {
		id: 'forge-ui.egressErrorMessage.egress-permissions-for',
		defaultMessage: 'Egress permissions for',
		description: 'Egress permissions missing in manifest message part 1',
	},
	missingInTheManifestForMoreInformation: {
		id: 'forge-ui.egressErrorMessage.missing-in-the-manifest-for-more-information',
		defaultMessage: 'missing in the manifest. For more information',
		description: 'Egress permissions missing in manifest message part 2',
	},
	seeReferenceDocs: {
		id: 'forge-ui.egressErrorMessage.see-reference-docs',
		defaultMessage: 'see reference docs',
		description: 'Egress permissions missing in manifest message part 3',
	},
	unableToDisplayDueToImageLinkIssue: {
		id: 'forge-ui.egressErrorMessage.unableToDisplayDueToImageLinkIssue',
		defaultMessage: 'Unable to display due to an image link issue.',
		description: 'Message for the egress error message',
	},
});

const useConditionalIntl = functionWithFG<() => any>(
	'forge-ui-add-use-intl-to-format-messages',
	useIntl,
	() => ({
		formatMessage: (_: any, fallback: string) => fallback
	}),
);

export const EgressErrorMessage = ({ url }: EgressErrorMessageProps): React.JSX.Element => {
	const { forgeEnvironment } = useContext(RendererContext);
	const dataUriRegex = /^data/;
	const isDataUri = dataUriRegex.test(url);

	const intl = useConditionalIntl();

	if (forgeEnvironment === 'DEVELOPMENT') {
		return (
			<SectionMessage appearance="error" testId="egress-error-message">
				{isDataUri ? (
					<Text>
						{intl!.formatMessage(messages.pleaseUseThe, 'Please use the')}
						{' '}
						<Text as="strong">
							{intl!.formatMessage(messages.dataImage, 'data:image')}
						</Text>
						{' '}
						{intl!.formatMessage(messages.schemeForAnImageSource, 'scheme for an image source')}
					</Text>
				) : (
					<Text>
						{intl!.formatMessage(messages.egressPermissionsFor, 'Egress permissions for')}
						{' '}
						<Link href={url} target="_blank">
							{url}
						</Link>
						{' '}
						{intl!.formatMessage(messages.missingInTheManifestForMoreInformation, 'missing in the manifest. For more information')}
						{' '}
						<Link
							href="https://developer.atlassian.com/platform/forge/manifest-reference/permissions/#images"
							target="_blank"
						>
							{intl!.formatMessage(messages.seeReferenceDocs, 'see reference docs')}
						</Link>
						.
					</Text>
				)}
			</SectionMessage>
		);
	}

	return (
		<SectionMessage appearance="error">
			{intl!.formatMessage(messages.unableToDisplayDueToImageLinkIssue, 'Unable to display due to an image link issue.')}
		</SectionMessage>
	);
};
